import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import { NvAccessDialog, NvCaseCard, NvCheckbox, NvEmptyState, NvErrorState, nvCaseStudyPath } from "@/components/navigator";
import { useAuth } from "@/contexts/auth-context";
import { useAccessGate } from "@/hooks/use-access-gate";
import { useRequest } from "@/hooks/use-request";
import { cn } from "@/lib/utils";
import { listCaseStudies } from "@/services/navigator/case-study-service";
import { CaseApplyDialog, CaseGuideDialog } from "./case-study-apply-dialogs";

// F02 Case Study 목록 (Figma 203:9111, IA 129~132). 산업 필터는 URL(?industry=IND-01,IND-02)에 반영해요.
// 비회원 · 승인 대기 · 공급자(△)는 카드만 보고, 필터 · 카드를 누르면 이용 권한 안내 (가정 A34).
export const CASE_LOGIN_MESSAGE = "Case Study 상세는\n로그인 후 이용할 수 있습니다.";
// Case Study 등록 신청(공급자)은 2차 반영 항목: 1차 오픈 화면에서 숨겨요 (API · 팝업 코드는 그대로 둠)
const CASE_APPLY_ENABLED = false;

export const CASE_FORBIDDEN_MESSAGE = "Case Study 상세는\n수요자 · PwC 내부 회원만 이용할 수 있습니다.";

export function NavigatorCaseStudyListPage() {
  const { isLoading, accessLevel } = useAuth();
  const gate = useAccessGate();
  // 공급자 [등록 안내] · [Case Study 등록 신청]은 2차 (Figma ver.6 "1차 오픈 (2차 제외)" 목록에는 없음, D44) — 켜려면 CASE_APPLY_ENABLED
  const isSupplier = CASE_APPLY_ENABLED && accessLevel === "supplier";
  const [dialog, setDialog] = useState<"guide" | "apply" | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = (searchParams.get("industry") ?? "").split(",").filter(Boolean);
  const selectedKey = selected.join(",");
  const { denied } = gate;

  // The previous list (and the industry counts in the sidebar) stays while a new filter loads;
  // a response that belongs to an older filter never replaces the current one.
  const { data, error, reload } = useRequest((signal) => listCaseStudies(selected, signal), [selectedKey], { keepPreviousData: true });

  const setIndustries = (codes: string[]) => {
    const next = new URLSearchParams(searchParams);
    if (codes.length) next.set("industry", codes.join(","));
    else next.delete("industry");
    setSearchParams(next, { replace: true });
  };
  const toggle = (code: string) => {
    if (denied) return gate.show(denied);
    setIndustries(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  };

  return (
    <div className="mx-auto w-full max-w-[1240px] px-3 pb-[100px]">
      <header className="flex items-end justify-between gap-6 pb-10 pt-14">
        <div className="flex flex-col gap-[14px]">
          <h1 className="text-[48px] font-normal leading-[55.7px] tracking-[-0.03em] text-nv-text-primary">Case Study</h1>
          <p className="text-[18px] leading-[21.6px] tracking-[-0.01em] text-nv-text-secondary">
            에이전트를 도입한 기업이 어떤 문제를 어떻게 풀었는지 확인하세요.
          </p>
        </div>
        {isSupplier && (
          <div className="flex shrink-0 items-center gap-5">
            <button
              type="button"
              onClick={() => setDialog("guide")}
              className="text-[15px] font-bold leading-[18px] text-nv-text-primary underline decoration-nv-brand decoration-2 underline-offset-4"
            >
              등록 안내
            </button>
            <button
              type="button"
              onClick={() => setDialog("apply")}
              className="flex h-14 w-[250px] items-center justify-between rounded-nv-pill bg-nv-brand px-6 text-[16px] font-bold text-nv-text-inverse hover:bg-nv-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand focus-visible:ring-offset-2"
            >
              Case Study 등록 신청
              <span aria-hidden className="text-[18px]">→</span>
            </button>
          </div>
        )}
      </header>

      <div className="flex items-start gap-10">
        <aside aria-label="산업 필터" className="w-[250px] shrink-0 rounded-nv-card bg-nv-bg-page px-[22px] pb-[14px] pt-[22px]">
          <h2 className="pb-3 text-[17px] font-medium leading-[20.4px] tracking-[-0.01em] text-nv-text-primary">산업</h2>
          <ul>
            <li className="flex h-10 items-center">
              <NvCheckbox
                tone="dark"
                boxClassName="border-[#CBD1D6]"
                checked={selected.length === 0}
                onCheckedChange={() => (denied ? gate.show(denied) : setIndustries([]))}
                label="전체"
                className="flex-1 gap-3"
                labelClassName="flex-1 text-[15px] leading-[18px] tracking-[-0.01em]"
              />
              <span className="text-[13px] leading-4 text-nv-text-secondary">{data?.total ?? ""}</span>
            </li>
            {data?.industries.map((industry) => {
              // 0건 산업은 회색 비활성(선택 불가) — 제안 17 · Figma ver.7. 주소로 이미 골라 둔 값은 풀 수 있게 둬요
              const empty = industry.count === 0 && !selected.includes(industry.code);
              return (
                <li key={industry.code} className={cn("flex h-10 items-center", empty && "opacity-40")}>
                  <NvCheckbox
                    tone="dark"
                    boxClassName="border-[#CBD1D6]"
                    checked={selected.includes(industry.code)}
                    disabled={empty}
                    onCheckedChange={() => toggle(industry.code)}
                    label={industry.label}
                    className={cn("flex-1 gap-3", empty && "opacity-100")}
                    labelClassName="flex-1 text-[15px] leading-[18px] tracking-[-0.01em]"
                  />
                  <span className="text-[13px] leading-4 text-nv-text-secondary">{industry.count}</span>
                </li>
              );
            })}
          </ul>
        </aside>

        <section aria-label="Case Study 목록" className="min-w-0 flex-1">
          {!!error && <NvErrorState variant="plain" error={error} onRetry={reload} className="py-20" />}
          {!error && data?.items.length === 0 && !isLoading && <NvEmptyState variant="plain">조건에 맞는 Case Study가 없습니다.</NvEmptyState>}
          <div className="grid grid-cols-3 gap-6">
            {data?.items.map((item) =>
              denied ? (
                <NvCaseCard key={item.id} item={item} realData onClick={() => gate.show(denied)} />
              ) : (
                <NvCaseCard key={item.id} item={item} realData to={nvCaseStudyPath(item.id)} />
              ),
            )}
          </div>
        </section>
      </div>

      <NvAccessDialog {...gate.dialogProps} loginMessage={CASE_LOGIN_MESSAGE} forbiddenMessage={CASE_FORBIDDEN_MESSAGE} />
      {isSupplier && (
        <>
          <CaseGuideDialog open={dialog === "guide"} onOpenChange={(open) => setDialog(open ? "guide" : null)} onApply={() => setDialog("apply")} />
          <CaseApplyDialog open={dialog === "apply"} onOpenChange={(open) => setDialog(open ? "apply" : null)} onShowGuide={() => setDialog("guide")} />
        </>
      )}
    </div>
  );
}
